import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Dialog, ErrorAlert, KeyedList, Loading } from '../components/kit.tsx';
import { command, query } from '../lib/api.ts';
import { useFormat } from '../lib/format.ts';
import { useSignedIn } from '../lib/session.tsx';

type OwnSession = {
  sessionRef: string; mode: 'standard' | 'shared'; clientLabel: string | null; createdAt: string; lastActivityAt: string;
  idleExpiresAt: string; absoluteExpiresAt: string; current: boolean;
};

/**
 * The person's own sessions (ASVS 3.3.4) and "sign out everywhere else" (3.3.3), both scheduled for the
 * Phase 3 UI by the project owner (2026-09-27). Ending a session signs that browser out and makes the
 * drafts left there unreadable.
 */
export function Sessions() {
  const { t } = useTranslation();
  const { view } = useSignedIn();
  const f = useFormat();
  const qc = useQueryClient();
  const [confirmAll, setConfirmAll] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const list = useQuery({ queryKey: ['session.mine'], queryFn: () => query<OwnSession[]>('session.mine'), enabled: !view.ghost });
  const end = useMutation({
    mutationFn: (ref: string) => command('session.end', { sessionRef: ref }),
    onSuccess: async () => {
      setNotice(t('sessions.ended'));
      await qc.invalidateQueries({ queryKey: ['session.mine'] });
    },
  });
  const endOthers = useMutation({
    mutationFn: () => command<{ ended: number }>('session.endOthers', {}),
    onSuccess: async (r) => {
      setConfirmAll(false);
      setNotice(t('sessions.endedOthers', { count: r.result.ended }));
      await qc.invalidateQueries({ queryKey: ['session.mine'] });
    },
  });

  if (view.ghost) return null;
  const others = list.data?.filter((s) => !s.current).length ?? 0;
  return (
    <section className="card" aria-labelledby="settings-sessions">
      <h2 id="settings-sessions">{t('sessions.title')}</h2>
      <p>{t('sessions.intro')}</p>
      {notice ? <Alert tone="success"><p>{notice}</p></Alert> : null}
      <ErrorAlert error={list.error ?? end.error ?? endOthers.error} />
      {list.isPending ? <Loading /> : null}
      {list.data ? (
        <KeyedList items={list.data} getKey={(s) => s.sessionRef} label={t('sessions.title')}>
          {(s) => (
            <div className="row" data-testid="session-row">
              <span>
                <strong>{s.clientLabel ?? t('sessions.unknownClient')}</strong>{' '}
                {s.current ? <span className="tag">{t('sessions.current')}</span> : null}{' '}
                {s.mode === 'shared' ? <span className="tag">{t('sessions.shared')}</span> : null}
                <br />
                <span className="muted small">
                  {t('sessions.times', { started: f.dateTime(s.createdAt), active: f.dateTime(s.lastActivityAt) })}
                </span>
              </span>
              {!s.current ? (
                <Button variant="secondary" busy={end.isPending && end.variables === s.sessionRef}
                  aria-label={t('sessions.endLabel', { client: s.clientLabel ?? t('sessions.unknownClient') })}
                  onClick={() => end.mutate(s.sessionRef)}>{t('sessions.end')}</Button>
              ) : null}
            </div>
          )}
        </KeyedList>
      ) : null}
      {others > 0 ? <Button variant="danger" onClick={() => setConfirmAll(true)}>{t('sessions.endOthers')}</Button> : null}
      <Dialog
        open={confirmAll}
        title={t('sessions.confirmTitle')}
        onCancel={() => setConfirmAll(false)}
        actions={(
          <>
            <Button variant="danger" busy={endOthers.isPending} onClick={() => endOthers.mutate()}>{t('sessions.endOthers')}</Button>
            <Button variant="secondary" onClick={() => setConfirmAll(false)}>{t('common.cancel')}</Button>
          </>
        )}
      >
        <p>{t('sessions.confirmBody', { count: others })}</p>
      </Dialog>
    </section>
  );
}
